import React from 'react';
import { Collapse, CollapseProps } from 'antd';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import DevicesContainer, {
  containerConfiguration as devicesContainerConfiguration,
} from '@totem/components/devices/devicesContainer/DevicesContainer';
import DeviceTable from '@totem/components/portfolio/buildings/details/DeviceTable';
import OpenBuildingSalesOrderLines from '@totem/components/salesOrders/building/OpenBuildingSalesOrderLines';
import GatewayDeviceTable from '@totem/components/services/remoteAccess/roleUsers/rootRole/GatewayDeviceTable';
import AuvikMappingContainer from '@totem/components/settings/sections/auvikMapping/auvikMappingContainer';
import BuildingMappingContainer from '@totem/components/settings/sections/nozomi/buildingMapping/BuildingMappingContainer';
import AuvikBuildingMappingTable from '@totem/components/ticketing/additional/buildingDecommissioning/auvik/AuvikBuildingMappingTable';
import NozomiBuildingMappingTable from '@totem/components/ticketing/additional/buildingDecommissioning/nozomi/NozomiBuildingMappingTable';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';
import { isNull } from '@totem/utilities/common';
import { GATEWAY_PAGINATION_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  ticket: TicketQueryResult;
};

const BuildingDecommissioningLayout = ({ ticket }: Props) => {
  const [totalNinjaOne, setTotalNinjaOne] = React.useState(0);

  if (isNull(ticket) || ticket.building.id === EMPTY_ID) {
    return <span />;
  }

  const items: CollapseProps['items'] = [
    {
      key: 'Remove Devices from NinjaOne / SentinelOne',
      label: 'Remove Devices from NinjaOne / SentinelOne',
      children: (
        <InputContainer
          filterConfig={devicesContainerConfiguration}
          defaultSort={{
            field: 'device.displayName',
            direction: 'ascend',
          }}
          staticFilters={[
            { field: 'building', values: [ticket.building.id] },
            { field: 'keyType', values: ['NinjaOne', 'SentinelOne'] },
          ]}
        >
          <DevicesContainer
            onRecordTotalChanged={(total) => setTotalNinjaOne(total)}
          >
            <IBCard
              isCovered
              title={'Devices'}
              extra={
                <>
                  <RecordCountBadge count={totalNinjaOne} />
                </>
              }
            >
              <DeviceTable />
            </IBCard>
          </DevicesContainer>
        </InputContainer>
      ),
    },
    {
      key: 'Remove Auvik',
      label: 'Remove Auvik',
      children: (
        <AuvikMappingContainer>
          <AuvikBuildingMappingTable buildingId={ticket.building.id} />
        </AuvikMappingContainer>
      ),
    },
    {
      key: 'Remove Nozomi',
      label: 'Remove Nozomi',
      children: (
        <BuildingMappingContainer>
          <NozomiBuildingMappingTable buildingId={ticket.building.id} />
        </BuildingMappingContainer>
      ),
    },
    {
      key: 'Remove Airwalls',
      label: 'Remove Airwalls',
      children: (
        <InputContainer
          filterConfig={devicesContainerConfiguration}
          defaultSort={{
            field: 'device.displayName',
            direction: 'ascend',
          }}
          staticFilters={[
            { field: 'building', values: [ticket.building.id] },
            { field: 'type', values: ['IB Gateway', 'Airwall'] },
          ]}
        >
          <DevicesContainer deviceEndPoint={GATEWAY_PAGINATION_ENDPOINT}>
            <GatewayDeviceTable />,
          </DevicesContainer>
        </InputContainer>
      ),
    },
    {
      key: 'Cancel Contract Lines',
      label: 'Cancel Contract Lines',
      children: <OpenBuildingSalesOrderLines buildingId={ticket.building.id} />,
    },
  ];

  return <Collapse items={items} defaultActiveKey={['1']} />;
};

export default BuildingDecommissioningLayout;
